import { useEffect, useState } from 'react';

import { CloseOutlined, EditOutlined } from '@ant-design/icons';
import { Flex } from '@revenuemarket/cds';
import { colorTokens } from '@revenuemarket/design';
import { ResponseStatus } from '@revenuemarket/http';
import { CircleMinus, CirclePlus, Toast } from '@revenuemarket/ui';
import { Search } from '@revenuemarket/ui/antd/Input';
import { Button, Modal, ModalProps, Segmented, Tooltip, Typography } from 'antd';
import dayjs, { Dayjs } from 'dayjs';

import CategorySimpleLabelSelctor from '~components/Labeling/LabelManagement/CategorySimpleLabelSelctor';
import GroupLabelSelect from '~components/Mapping/GroupLabelSelect';
import {
  MappingTotal,
  TaxInvoiceMappingItem,
  TransactionMappingItem,
} from '~components/Mapping/MappingItem';
import DateRangePickerWithPreset, {
  DatePickerRangeType,
} from '~components/ui/DatePicker/DateRangePickerWithPreset';
import { flattenTree } from '~components/ui/HorizontalTreeTable/Table';
import { useCategories, useCategoriesTree } from '~hooks/queries/categories/query';
import { useMappingTransactionToTaxInvoice } from '~hooks/queries/mapping/query';
import { useGetTaxInvoiceMappingsById } from '~hooks/queries/tax-invoice/query';
import { useDemoAwareCompanyId } from '~hooks/useDemoAwareCompanyId';
import { css } from '~libs/emotion';
import { MIXPANEL_EVENT } from '~libs/mixpanel/event';
import { CashFlowType } from '~models/cashFlow';
import { TaxInvoiceType } from '~models/tax-invoice';
import { mixpanel } from '~pages/_app';

import AdjustmentTaxInvoices from './AdjustmentTaxInvoices';
import { formatAdjustment, fotmatTransactions } from './SettlementListDrawer';
import useSettlementMappingStore from './SettlementMappingStore';
import SettlementTargetListTable from './SettlementTargetList';
import { formatNumByTaxInvoice } from './utils';

interface Props extends ModalProps {
  taxInvoiceId: string;
  taxInvoiceType: TaxInvoiceType;
  targetDate: string;
  onMappingSuccess: () => void;
  onClose?: () => void;
}

const SettlementMappingModal: React.FC<Props> = ({
  taxInvoiceId,
  taxInvoiceType,
  targetDate,
  onMappingSuccess,
  onClose,
  ...props
}) => {
  const { companyId } = useDemoAwareCompanyId();
  const [rangeType, setRangeType] = useState<DatePickerRangeType>('month');
  const [searchParam, setSearchParam] = useState<string>('');
  const [cashFlowType, setCashFlowType] = useState<CashFlowType>(CashFlowType.IN);
  const { mutateAsync: mapTransactionsToTaxInvoice, isLoading } =
    useMappingTransactionToTaxInvoice();
  const [rangeDateValue, setRangeDateValue] = useState<[Dayjs, Dayjs]>([
    dayjs(targetDate).subtract(2, 'M').startOf('month'),
    dayjs(targetDate).add(2, 'M').endOf('month'),
  ]);

  useEffect(() => {
    mixpanel.track(MIXPANEL_EVENT.VIEW.SETTLEMENT_MAPPING_MODAL);
  }, []);

  const startDate = rangeDateValue[0];
  const endDate = rangeDateValue[1];

  const {
    mappingTransactions,
    deleteMappingTransaction,
    setAdjustmentTaxInvoice,
    editMappingTransaction,
    deleteAdjustmentTaxInvoice,
    addAdjustmentTaxInvoice,
    adjustmentTaxInvoices,
    initialize,
    reset,
  } = useSettlementMappingStore();

  const { data } = useCategories({
    companyId,
  });

  const { data: treeData } = useCategoriesTree({ companyId });
  const categoryTrees = treeData?.Data?.data?.tree?.children;

  const result = useGetTaxInvoiceMappingsById({
    companyId,
    taxInvoiceId,
    type: taxInvoiceType,
  });

  const mapping = result.data?.Data?.data;

  useEffect(() => {
    if (targetDate) {
      setRangeDateValue([
        dayjs(targetDate).subtract(2, 'M').startOf('month'),
        dayjs(targetDate).add(2, 'M').endOf('month'),
      ]);
    }
  }, [targetDate]);

  useEffect(() => {
    if (mapping) {
      if (mapping.taxInvoiceType === 'PURCHASE') {
        setCashFlowType(CashFlowType.OUT);
      }

      initialize({
        adjustmentTaxInvoices: formatAdjustment(mapping.mappedInvoiceAdjustments, taxInvoiceType),
        originalMappingTransactions: fotmatTransactions(mapping.mappedTransactionLabels),
        mappingTransactions: fotmatTransactions(mapping.mappedTransactionLabels),
      });
    }
  }, [mapping]);

  if (!mapping) return null;

  const mappingAmount = mappingTransactions.reduce((acc, cur) => {
    if (mapping.taxInvoiceType === 'SALES') {
      return acc + (cur.itemCashFlowType === 'IN' ? cur.mappingAmount : -cur.mappingAmount);
    } else {
      return acc + (cur.itemCashFlowType === 'OUT' ? cur.mappingAmount : -cur.mappingAmount);
    }
  }, 0);
  const targetAmount =
    mapping.totalAmount -
    adjustmentTaxInvoices.reduce((acc, cur) => acc + (cur?.adjustmentAmount ?? 0), 0);
  const targetUnMappedAmount = targetAmount - mappingAmount;
  const unMappingAmount = targetUnMappedAmount;

  const onMapping = async () => {
    const response = await mapTransactionsToTaxInvoice({
      companyId,
      taxInvoiceId,
      type: taxInvoiceType,
      data: {
        transactionLabels: mappingTransactions.map((m) => {
          const isFx = m.isFx;

          const fxAmount = parseFloat(
            ((Math.abs(m.mappingAmount / m.totalAmount) * m.labelAmount * 100) / 100).toFixed(12),
          );

          const amount = isFx ? fxAmount : m.mappingAmount;

          return {
            labelId: m.labelId,
            transactionId: m.transactionId,
            categoryId: m?.categoryLabelKey?.split('_')[0]
              ? Number(m?.categoryLabelKey?.split('_')[0])
              : 0,
            companyDetailedCategoryId: m.categoryLabelKey?.split('_')[1]
              ? Number(m.categoryLabelKey?.split('_')[1])
              : null,
            customLabelId: m.groupLabelId,
            mappingAmount: amount * (m.cashFlowType === 'IN' ? 1 : -1),
          };
        }),
        invoiceAdjustments: adjustmentTaxInvoices.map((a) => ({
          adjustmentAmount: formatNumByTaxInvoice(a?.adjustmentAmount ?? 0, taxInvoiceType),
          reportingDate: a?.reportingDate ?? '',
          memo: a?.memo ?? '',
        })),
      },
    });

    if (response.Status === ResponseStatus.SUCCESS) {
      Toast.success('정산 내역이 저장되었습니다.');
      mixpanel.track(MIXPANEL_EVENT.SETTLEMENT.MAPPING, {
        type: taxInvoiceType,
      });
      mixpanel.track(MIXPANEL_EVENT.ENGAGEMENT.DEFAULT, {
        type: taxInvoiceType === 'PURCHASE' ? 'settle purchases' : 'settle sales',
      });
      onMappingSuccess();
      onClose?.();
    } else {
      Toast.error('정산 내역 저장에 실패했습니다.');
    }
  };

  const disabled =
    mappingTransactions.some((m) => !m.categoryLabelKey) ||
    !adjustmentTaxInvoices.every((a) => Boolean(a?.memo)) ||
    unMappingAmount < 0;

  const unMappingAmountErrorText =
    '정산 금액은 세금계산서 금액을 초과할 수 없습니다. 조정을 눌러 금액을 맞춰주세요';

  return (
    <Modal
      centered
      width="90vw"
      open={props.open}
      onCancel={() => {
        reset();
        onClose?.();
      }}
      footer={null}
      title={
        <Flex direction="column" gap={4}>
          <Typography.Title level={5}>정산 내역 찾기</Typography.Title>
          <Typography.Text
            style={{
              fontWeight: 400,
              fontSize: 14,
            }}
          >
            오른쪽 세금계산서에 해당하는 정산 내역을 찾아 선택해주세요.
          </Typography.Text>
        </Flex>
      }
    >
      <Flex
        gap={24}
        style={{
          marginTop: 24,
        }}
      >
        <Flex direction="column" gap={16} style={{ width: '70%' }}>
          <Flex gap={8} align="center">
            <DateRangePickerWithPreset
              rangeType={rangeType}
              onChangeRangeType={(value) => {
                setRangeType(value);
              }}
              value={rangeDateValue}
              datePickerProps={{
                disabledDate: (current) => {
                  // 오늘 이후는 선택 불가
                  return current && current > dayjs().endOf('day');
                },
              }}
              onDateRangeCompleted={(tempChangedValues) => {
                setRangeDateValue([
                  tempChangedValues[0]?.startOf('month'),
                  tempChangedValues[1]?.endOf('month'),
                ] as [Dayjs, Dayjs]);
              }}
            />
            <Segmented
              value={cashFlowType}
              onChange={(value) => {
                setCashFlowType(value as CashFlowType);
              }}
              options={[
                { label: '입금', value: 'IN' },
                { label: '출금', value: 'OUT' },
              ]}
            />
            <Search
              placeholder="금액, 적요, 거래자명 검색"
              style={{
                width: 590,
              }}
              onSearchChange={(value) => {
                setSearchParam(value);
              }}
            />
          </Flex>
          <SettlementTargetListTable
            startDate={startDate}
            endDate={endDate}
            cashFlowType={cashFlowType}
            searchParam={searchParam}
          />
        </Flex>
        <Flex
          direction="column"
          style={{
            width: '30%',
            flex: 1,
          }}
        >
          <div
            style={{
              background: '#F0FFFC',
              padding: '15px 16px 13px 16px',
              borderRadius: 8,
            }}
          >
            {mapping && (
              <TaxInvoiceMappingItem
                {...{
                  type: mapping?.taxInvoiceType ?? 'PURCHASE',
                  date: mapping.reportingDate,
                  businessRegistrationNumber: mapping.counterPartyBusinessRegNumber,
                  contractorName: mapping.counterPartyCompanyName,
                  mappingAmount: mapping.totalAmount,
                  totalAmount: mapping.totalAmount,
                  repItem: mapping.repItems,
                  counterPartyName: mapping.counterPartyName,

                  active: true,
                }}
                active
              />
            )}
          </div>
          <div
            style={{
              overflowY: 'auto',
              maxHeight: '50vh',
            }}
          >
            {mappingTransactions.map((m, idx) => {
              const flattenedTree = flattenTree(categoryTrees ?? [])
                .filter((item) => !!item.value?.name)
                .filter((treeItem) => treeItem.value?.cashFlowType === m.cashFlowType);
              const targetCategory = m.categoryLabelKey
                ? flattenedTree.find(
                    (treeItem) => treeItem.value?.categoryLabelKey === m.categoryLabelKey,
                  )
                : undefined;

              const type =
                mapping.taxInvoiceType === 'SALES'
                  ? m.cashFlowType === CashFlowType.IN
                    ? 'minus'
                    : 'plus'
                  : m.cashFlowType === CashFlowType.OUT
                    ? 'minus'
                    : 'plus';
              return (
                <Flex gap={8} key={m.transactionId}>
                  <Flex
                    justify="center"
                    style={{
                      height: 'inherit',
                      position: 'relative',
                      width: 16,
                      marginLeft: 8,
                    }}
                  >
                    <div
                      style={{
                        width: 1,
                        height: '100%',
                        background: colorTokens.borderDefault,
                      }}
                    />
                    <Flex
                      style={{ height: 28, position: 'absolute', top: 11, left: 0 }}
                      align="center"
                    >
                      {type ? (
                        type === 'minus' ? (
                          <CircleMinus
                            style={{
                              background: 'white',
                            }}
                          />
                        ) : (
                          <CirclePlus
                            style={{
                              background: 'white',
                            }}
                          />
                        )
                      ) : (
                        <div />
                      )}
                    </Flex>
                  </Flex>
                  <div
                    style={{
                      position: 'relative',
                      width: '100%',
                      padding: '15px 16px 13px 0px',
                    }}
                  >
                    <CloseOutlined
                      style={{
                        position: 'absolute',
                        right: 16,
                        top: 16,
                        cursor: 'pointer',
                      }}
                      onClick={() => deleteMappingTransaction(idx)}
                    />
                    <Flex direction="column" gap={8}>
                      <TransactionMappingItem
                        cashFlowType={m.cashFlowType}
                        transactionName={m.transactionName}
                        transactionDescription={m.transactionDescription}
                        transactionAt={m.transactionAt}
                        mappingAmount={m.mappingAmount}
                        amount={m.totalAmount}
                      />
                      <Flex gap={8} align="center">
                        <CategorySimpleLabelSelctor
                          autoClose
                          treeList={flattenedTree}
                          onSelect={(categoryLabelKey) => {
                            editMappingTransaction(
                              {
                                ...m,
                                categoryLabelKey,
                              },
                              idx,
                            );
                          }}
                          targetCategory={targetCategory}
                        />

                        <GroupLabelSelect
                          style={{ flex: 1 }}
                          value={m.groupLabelId}
                          onChange={(value) => {
                            editMappingTransaction(
                              {
                                ...m,
                                groupLabelId: value,
                              },
                              idx,
                            );
                          }}
                        />
                      </Flex>
                    </Flex>
                  </div>
                </Flex>
              );
            })}
            {adjustmentTaxInvoices.length > 0 ? (
              adjustmentTaxInvoices.map((adjustmentTaxInvoice, idx) => {
                return (
                  <div
                    style={{
                      position: 'relative',
                    }}
                  >
                    <AdjustmentTaxInvoices
                      adjustmentTaxInvoice={adjustmentTaxInvoice}
                      setAdjustmentTaxInvoice={setAdjustmentTaxInvoice}
                      deleteAdjustmentTaxInvoice={deleteAdjustmentTaxInvoice}
                      idx={idx}
                    />
                  </div>
                );
              })
            ) : (
              <div />
            )}
          </div>
          <div
            style={{
              marginTop: adjustmentTaxInvoices.length + mappingTransactions.length === 0 ? 13 : 0,
            }}
          >
            <MappingTotal
              unMappingAmount={unMappingAmount}
              unmappingAmountError={unMappingAmount !== 0}
              unmappingErrorText={unMappingAmountErrorText}
              unmappingAmountLabel={
                <Flex gap={8} align="center">
                  <Typography.Text
                    style={{
                      fontSize: 14,
                    }}
                  >
                    {mapping.taxInvoiceType === 'PURCHASE' ? '매입' : '매출'} 잔액
                  </Typography.Text>

                  <Button
                    type="link"
                    icon={
                      <EditOutlined
                        style={{
                          color: colorTokens.iconDefault,
                        }}
                      />
                    }
                    onClick={() => {
                      addAdjustmentTaxInvoice(
                        dayjs(mapping.reportingDate).format('YYYY-MM-DD'),
                        unMappingAmount,
                      );
                    }}
                  >
                    <Typography.Text
                      style={{
                        color: colorTokens.textDescription,
                      }}
                    >
                      조정
                    </Typography.Text>
                  </Button>
                </Flex>
              }
            />
          </div>
          <Flex
            gap={8}
            align="center"
            style={{
              marginTop: 16,
            }}
          >
            <Button
              style={{
                width: 100,
              }}
              onClick={() => onClose?.()}
            >
              취소
            </Button>
            <Tooltip
              className={css`
                width: 100%;
              `}
              title={unMappingAmount < 0 ? unMappingAmountErrorText : ''}
            >
              <Button
                style={{
                  width: '100%',
                }}
                disabled={disabled}
                type="primary"
                loading={isLoading}
                onClick={onMapping}
              >
                정산 내역 저장
              </Button>
            </Tooltip>
          </Flex>
        </Flex>
      </Flex>
    </Modal>
  );
};

export default SettlementMappingModal;
